"use strict";

// Copyright 2013-2022 The MathWorks, Inc.

/**
 * This class is responsible for keeping track of all the endPoints with which remote layer
 * communicates. It provides a uniform API to create new endpoint, associate messages with that
 * endpoint, enable or disable the endpoint, create iframeProxy, for the endPoint
 */
define(['dojo/_base/declare', 'dojo/_base/array', 'dojo/_base/Deferred', 'dojo/_base/lang', './iframeproxy/IframeProxyClient'], function (declare, arrayUtils, Deferred, lang, IframeProxyClient) {
  return declare([], {
    /**
     * Initializes the endPoints Collection.
     */
    constructor: function constructor() {
      this._initializeEndPointsCollection();
    },

    /**
     * Sets up the endPoints
     * @private
     */
    _initializeEndPointsCollection: function _initializeEndPointsCollection() {
      this.endPointsCollection = {
        ROUTER_LEAF_INSECURE_ENDPOINT: {
          routingHost: '',
          routingKey: '',
          iframeProxy: null,
          enabled: true,
          iframeProxyUrl: '',
          pathSuffix: 'messageservice/json',
          messages: ['RouterLogin', 'CreateAccount', 'CurrentUser', 'Login', 'UpdateAccount', 'ServiceRegistry', 'ClientType', 'AuthorizationDetails', 'AcquireMatlab'],
          iframeProxyName: 'remoteProducer',
          withCredentials: true,
          acceptsMessageContainerMessages: true
        },
        ROUTER_LEAF_SECURE_ENDPOINT: {
          routingHost: '',
          routingKey: '',
          iframeProxy: null,
          enabled: true,
          iframeProxyUrl: '',
          pathSuffix: 'messageservice/json/secure',
          messages: [],
          iframeProxyName: 'remoteProducer',
          withCredentials: true,
          acceptsMessageContainerMessages: true
        },
        WORKER_ENDPOINT: {
          routingHost: '',
          routingKey: '',
          iframeProxy: null,
          enabled: false,
          iframeProxyUrl: '',
          pathSuffix: 'messageservice/json/secure',
          messages: [],
          iframeProxyName: 'remoteWorkerProducer',
          withCredentials: true,
          acceptsMessageContainerMessages: true
        },
        WORKER_ASYNC_ENDPOINT: {
          routingHost: '',
          routingKey: '',
          iframeProxy: null,
          enabled: true,
          iframeProxyUrl: '',
          pathSuffix: 'messageservice/async',
          messages: [],
          iframeProxyName: 'remoteWorkerAsyncProducer',
          withCredentials: true,
          acceptsMessageContainerMessages: true
        },
        USAGE_DATA_ROUTER_ENDPOINT: {
          routingHost: '',
          routingKey: '',
          iframeProxy: null,
          enabled: true,
          iframeProxyUrl: '',
          pathSuffix: 'udc/service/v1/events',
          messages: ['UsageDataEventCollection'],
          iframeProxyName: 'remoteUDCProducer',
          withCredentials: false,
          acceptsMessageContainerMessages: false
        },
        HELP_DATA_ROUTER_ENDPOINT: {
          routingHost: '',
          routingKey: '',
          iframeProxy: null,
          enabled: true,
          iframeProxyUrl: '',
          pathSuffix: '',
          messages: ['HelpDataEventCollection'],
          iframeProxyName: 'doc_center',
          withCredentials: false,
          acceptsMessageContainerMessages: false
        }
      };
    },

    /**
     * This function is used to rest the EndPointsManager's endPointsCollection.
     */
    reset: function reset() {
      this._initializeEndPointsCollection();
    },

    /**
     * This can be used to set up an endPoint during runtime.
     * @param endPointName : Name of the endPoint
     * @param isEnabled : During the initialization, the endPoint can be enabled or disabled
     * using this param
     * @param pathSuffix : This is the suffix of the URL to which we post messages.
     * @param IframeProxyName: Name of the iframe created.
     * @param isMessageContainerBased: This endpoint can be used to determine if it is message container Based or not
     */
    setUpNewEndPoint: function setUpNewEndPoint(endPointName, isEnabled, pathSuffix, IframeProxyName, isMessageContainerBased) {
      this.endPointsCollection[endPointName] = {
        routingHost: '',
        routingKey: '',
        pathSuffix: pathSuffix,
        enabled: isEnabled,
        messages: [],
        iframeProxy: null,
        iframeProxyName: IframeProxyName,
        acceptsMessageContainerMessages: isMessageContainerBased
      };
    },

    /**
     * returns the suffix of endPoint or URL.
     * @param endPointName
     * @return {string}
     */
    getPathSuffixForEndPoint: function getPathSuffixForEndPoint(endPointName) {
      return this.endPointsCollection[endPointName].pathSuffix;
    },

    /**
     * set the suffix for endPoint or URL.
     * @param endPointName
     * @param pathSuffix
     */
    setPathSuffixForEndPoint: function setPathSuffixForEndPoint(endPointName, pathSuffix) {
      this.endPointsCollection[endPointName].pathSuffix = pathSuffix;
    },

    /**
     * This function returns array of endPoint names. This ensures that endPointsCollection is
     * not modified outside this manager.
     * @return {} - Object containing endPointNames
     */
    getEndPointNames: function getEndPointNames() {
      var endPointsEnumeration = {};
      var endPointName;

      for (endPointName in this.endPointsCollection) {
        if (this.endPointsCollection.hasOwnProperty(endPointName)) {
          endPointsEnumeration[endPointName] = endPointName;
        }
      }

      return endPointsEnumeration;
    },

    /**
     * This function is used to set Routing Host and Routing Key for the endPoint. IframeProxy property
     * is also set up as a part of it.
     * @param endPointName
     * @param routingHost
     * @param routingKey
     */
    setRoutingHostAndRoutingKey: function setRoutingHostAndRoutingKey(endPointName, routingHost, routingKey) {
      if (routingHost && routingHost.lastIndexOf('/') !== routingHost.length - 1) {
        routingHost += '/';
      }

      this.endPointsCollection[endPointName].routingHost = routingHost;

      if (routingKey) {
        this.endPointsCollection[endPointName].routingKey = routingKey;
      }

      this._setIframeRoutingHostAndRoutingkey(endPointName);
    },

    /**
     * This function is used to set up the iframe for the endPoint.
     * @param endPointName
     * @private
     */
    _setIframeRoutingHostAndRoutingkey: function _setIframeRoutingHostAndRoutingkey(endPointName) {
      var iframeProxyClient = this.getIframeProxyClientForEndPoint(endPointName);

      if (iframeProxyClient) {
        iframeProxyClient.setRemotePageHost(this.endPointsCollection[endPointName].routingHost, this.endPointsCollection[endPointName].routingKey, null, this.endPointsCollection[endPointName].iframeProxyUrl, {
          withCredentials: this.endPointsCollection[endPointName].withCredentials
        });
      }
    },

    /**
     * Returns routingHost for the endPoint.
     * @param endPointName
     * @return routingHost
     */
    getRoutingHost: function getRoutingHost(endPointName) {
      return this.endPointsCollection[endPointName].routingHost;
    },

    /**
     * Returns Routing Key for the endPoint.
     * @param endPointName
     * @return {}
     */
    getRoutingKey: function getRoutingKey(endPointName) {
      return this.endPointsCollection[endPointName].routingKey;
    },

    /**
     * Returns messageTypes that are mapped to endPoint
     * @param endPointName
     * @return {*}
     */
    getMessageTypesMappedToEndPoint: function getMessageTypesMappedToEndPoint(endPointName) {
      return this.endPointsCollection[endPointName].messages;
    },

    /**
     * Returns boolean if the endPoint is enabled or not.
     * @param endPointName
     * @return boolean : true | false
     */
    isEndPointEnabled: function isEndPointEnabled(endPointName) {
      return this.endPointsCollection[endPointName].enabled;
    },

    /**
     * Maps message types to endPoints
     * @param endPointName
     * @param messages
     */
    setMessageTypesToEndPoint: function setMessageTypesToEndPoint(endPointName, messages) {
      this.endPointsCollection[endPointName].messages = messages;
    },

    /**
     * Sets the endPoint's isEnabled status to true
     * @param endPointName
     */
    enableEndPoint: function enableEndPoint(endPointName) {
      this.endPointsCollection[endPointName].enabled = true;
    },

    /**
     * Disables endPoint
     * @param endPointName
     */
    disableEndPoint: function disableEndPoint(endPointName) {
      this.endPointsCollection[endPointName].enabled = false;
    },

    /**
     * Gets appropriate endPoint based on the messageType passed. If an enabled endPoint is not
     * found, default endpoint ROUTER_LEAF_SECURE_ENDPOINT is returned.
     * @param messageType
     * @return String
     */
    getEndPointForMessageType: function getEndPointForMessageType(messageType) {
      var endPointType = null;
      var endPointName;

      for (endPointName in this.endPointsCollection) {
        if (this.endPointsCollection.hasOwnProperty(endPointName)) {
          endPointType = arrayUtils.lastIndexOf(this.endPointsCollection[endPointName].messages, messageType) > -1 ? endPointName : endPointType;

          if (endPointType) {
            if (this.isEndPointEnabled(endPointType)) {
              break;
            } else {
              // Set it back to null so that the default routing is applied.
              endPointType = null;
            }
          }
        }
      }

      return endPointType || this.getEndPointNames().ROUTER_LEAF_SECURE_ENDPOINT;
    },

    /**
     * Returns complete URL to which we must post messages.
     * @param endPointName
     * @return {*} String
     */
    getRemoteUrlForEndPoint: function getRemoteUrlForEndPoint(endPointName) {
      var url = this.endPointsCollection[endPointName].routingHost + this.endPointsCollection[endPointName].pathSuffix;

      if (this.endPointsCollection[endPointName].routingKey) {
        url = url + '?routingkey=' + this.endPointsCollection[endPointName].routingKey;
      }

      return url;
    },

    /**
     * Sets iframeProxyClient for the endPoint.
     * @param endPointName
     * @param iframeProxyClient
     */
    setIframeProxyClientForEndPoint: function setIframeProxyClientForEndPoint(endPointName, iframeProxyClient) {
      this.endPointsCollection[endPointName].iframeProxy = iframeProxyClient;
    },

    /**
     * gets IframeProxyClient for the endpoint.
     * @param endPointName
     * @return {*}
     */
    getIframeProxyClientForEndPoint: function getIframeProxyClientForEndPoint(endPointName) {
      var iframeProxyClient = this.endPointsCollection[endPointName].iframeProxy;

      if (!iframeProxyClient) {
        iframeProxyClient = new IframeProxyClient(this.endPointsCollection[endPointName].iframeProxyName);
        this.endPointsCollection[endPointName].iframeProxy = iframeProxyClient;
      }

      return iframeProxyClient;
    },

    /**
     * This function can be used to determine if the endPoint is message container based or not.
     * @param endPointName
     * @return {boolean}
     */
    isEndpointMessageContainerBased: function isEndpointMessageContainerBased(endPointName) {
      if (endPointName && this.endPointsCollection[endPointName]) {
        return this.endPointsCollection[endPointName].acceptsMessageContainerMessages;
      } else {
        return true;
      }
    },

    /**
     * Adds messageTypes to the endpoint.
     * @param endPointName
     * @param message
     */
    addMessagesTypesToEndPoint: function addMessagesTypesToEndPoint(endPointName, message) {
      if (this.endPointsCollection[endPointName] && this.endPointsCollection[endPointName].messages.indexOf(message) === -1) {
        this.endPointsCollection[endPointName].messages.push(message);
      }
    },

    /**
     * Removes messageTypes from the endPoint.
     * @param endPointName
     * @param message
     */
    removeMessageTypesFromEndPoint: function removeMessageTypesFromEndPoint(endPointName, message) {
      if (this.endPointsCollection[endPointName]) {
        var index = this.endPointsCollection[endPointName].messages.indexOf(message);

        if (index > -1) {
          this.endPointsCollection[endPointName].messages.splice(index, 1);
        }
      }
    }
  });
});
//# sourceMappingURL=EndPointsManager.js.map
